import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, Trash2, RefreshCw, Search, MoreVertical, FileText, AlertCircle, CheckCircle, Clock, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { reconciliationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { BankStatement, StatementStatus } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'
import { format } from 'date-fns'

const STATUS_CONFIG: Record<StatementStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  PENDING: { label: 'Pending', variant: 'outline', icon: <Clock className="h-3 w-3" /> },
  PROCESSING: { label: 'Processing', variant: 'secondary', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
  COMPLETED: { label: 'Completed', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
  FAILED: { label: 'Failed', variant: 'destructive', icon: <AlertCircle className="h-3 w-3" /> },
}

export default function StatementsPage() {
  const { canAddEdit } = usePermissions()
  const canEdit = canAddEdit('RECONCILIATION')
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()

  const { data: statementsData, isLoading } = useQuery({
    queryKey: ['statements'],
    queryFn: () => reconciliationApi.statements.list(),
  })

  const { data: accountsData } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => reconciliationApi.bankAccounts.list(),
  })

  const uploadMutation = useMutation({
    mutationFn: ({ accountId, file }: { accountId: string; file: File }) =>
      reconciliationApi.statements.upload(accountId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements'] })
      toast.success('Statement uploaded successfully')
      handleCloseUpload()
    },
    onError: () => toast.error('Failed to upload statement'),
  })

  const deleteMutation = useMutation({
    mutationFn: reconciliationApi.statements.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements'] })
      toast.success('Statement deleted successfully')
    },
    onError: () => toast.error('Failed to delete statement'),
  })

  const reprocessMutation = useMutation({
    mutationFn: reconciliationApi.statements.reprocess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements'] })
      toast.success('Statement reprocessing started')
    },
    onError: () => toast.error('Failed to reprocess statement'),
  })

  const handleCloseUpload = () => {
    setIsUploadOpen(false)
    setSelectedAccountId('')
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleUpload = () => {
    if (!selectedAccountId || !selectedFile) {
      toast.error('Please select a bank account and file')
      return
    }
    uploadMutation.mutate({ accountId: selectedAccountId, file: selectedFile })
  }

  const filteredStatements = (statementsData?.statements || []).filter(statement =>
    statement.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    statement.bank_account_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bank Statements</h1>
          <p className="text-muted-foreground">Upload and manage bank statements for reconciliation</p>
        </div>
        {canEdit && (
          <Button onClick={() => setIsUploadOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Statement
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search statements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Bank Account</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredStatements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">No statements found</TableCell>
                </TableRow>
              ) : (
                filteredStatements.map((statement) => (
                  <StatementRow
                    key={statement.statement_id}
                    statement={statement}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    onReprocess={(id) => reprocessMutation.mutate(id)}
                    canAddEdit={canEdit}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UploadDialog
        isOpen={isUploadOpen}
        onClose={handleCloseUpload}
        onUpload={handleUpload}
        accounts={accountsData?.bank_accounts || []}
        selectedAccountId={selectedAccountId}
        setSelectedAccountId={setSelectedAccountId}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        fileInputRef={fileInputRef}
        isLoading={uploadMutation.isPending}
      />
    </div>
  )
}

interface StatementRowProps {
  statement: BankStatement
  onDelete: (id: string) => void
  onReprocess: (id: string) => void
  canAddEdit: boolean
}

function StatementRow({ statement, onDelete, onReprocess, canAddEdit }: StatementRowProps) {
  const statusConfig = STATUS_CONFIG[statement.status]
  const dateRange = statement.start_date && statement.end_date
    ? `${format(new Date(statement.start_date), 'MMM d')} - ${format(new Date(statement.end_date), 'MMM d, yyyy')}`
    : '-'

  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          {statement.filename}
        </div>
      </TableCell>
      <TableCell>{statement.bank_account_name || '-'}</TableCell>
      <TableCell>{dateRange}</TableCell>
      <TableCell>{statement.transaction_count}</TableCell>
      <TableCell>
        <Badge variant={statusConfig.variant} className="gap-1">
          {statusConfig.icon}
          {statusConfig.label}
        </Badge>
      </TableCell>
      <TableCell>{format(new Date(statement.uploaded_at), 'MMM d, yyyy')}</TableCell>
      <TableCell>
        {canAddEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {statement.status === 'FAILED' && (
                <DropdownMenuItem onClick={() => onReprocess(statement.statement_id)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprocess
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(statement.statement_id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  )
}

interface UploadDialogProps {
  isOpen: boolean
  onClose: () => void
  onUpload: () => void
  accounts: { bank_account_id: string; account_name: string }[]
  selectedAccountId: string
  setSelectedAccountId: (id: string) => void
  selectedFile: File | null
  setSelectedFile: (file: File | null) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  isLoading: boolean
}

function UploadDialog({
  isOpen,
  onClose,
  onUpload,
  accounts,
  selectedAccountId,
  setSelectedAccountId,
  selectedFile,
  setSelectedFile,
  fileInputRef,
  isLoading,
}: UploadDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Bank Statement</DialogTitle>
          <DialogDescription>
            Upload a CSV, XLS, XLSX, or PDF file containing your bank transactions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Bank Account *</Label>
            <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a bank account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.bank_account_id} value={account.bank_account_id}>
                    {account.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Statement File *</Label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx,.pdf"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onUpload} disabled={isLoading || !selectedAccountId || !selectedFile}>
            {isLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

